import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getDateInputValue } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tasks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '', task_type: 'Call', related_to: 'client', related_id: '',
    due_date: '', priority: 'Medium', status: 'Pending', notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Round 44 · Item 3: honour /tasks?edit={task_id} deep-links from email
  // reminders and WhatsApp digest so agents jump straight into the exact
  // task edit dialog. Same pattern as Policies/Leads/Clients.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || tasks.length === 0) return;
    const match = tasks.find(t => t.task_id === editId);
    if (match) {
      openEditDialog(match);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchParams]);

  const loadData = async () => {
    try {
      const [tasksRes, clientsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/leads`, { withCredentials: true })
      ]);
      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.task_id}`, formData, { withCredentials: true });
        toast.success('Task updated successfully');
      } else {
        await axios.post(`${API}/tasks`, formData, { withCredentials: true });
        toast.success('Task created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      toast.success('Task deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const markComplete = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.task_id}`, { status: 'Completed' }, { withCredentials: true });
      toast.success('Task marked as completed');
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      task_type: task.task_type,
      related_to: task.related_to,
      related_id: task.related_id,
      due_date: getDateInputValue(task.due_date),
      priority: task.priority,
      status: task.status,
      notes: task.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '', task_type: 'Call', related_to: 'client', related_id: '',
      due_date: '', priority: 'Medium', status: 'Pending', notes: ''
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-orange-100 text-orange-800',
      'Low': 'bg-blue-100 text-blue-800'
    };
    return colors[priority] || 'bg-slate-100 text-slate-800';
  };

  const getRelatedName = (relatedTo, relatedId) => {
    if (relatedTo === 'client') {
      const client = clients.find(c => c.client_id === relatedId);
      return client ? client.name : 'Unknown';
    } else {
      const lead = leads.find(l => l.lead_id === relatedId);
      return lead ? lead.name : 'Unknown';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Tasks & Follow-ups</h1>
          <p className="text-slate-600 mt-1">Manage your daily activities</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-task-button">
              <Plus className="h-4 w-4 mr-2" /> Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
              <DialogDescription>Fill in the task details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input id="title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="task-title-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task_type">Task Type</Label>
                  <Select value={formData.task_type} onValueChange={(value) => setFormData({...formData, task_type: value})}>
                    <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Call">Call</SelectItem>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="related_to">Related To</Label>
                  <Select value={formData.related_to} onValueChange={(value) => setFormData({...formData, related_to: value, related_id: ''})}>
                    <SelectTrigger data-testid="task-related-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="related_id">Select {formData.related_to === 'client' ? 'Client' : 'Lead'} *</Label>
                  <Select value={formData.related_id} onValueChange={(value) => setFormData({...formData, related_id: value})} required>
                    <SelectTrigger data-testid="task-related-id-select"><SelectValue placeholder={`Select ${formData.related_to}`} /></SelectTrigger>
                    <SelectContent>
                      {formData.related_to === 'client' ? (
                        clients.map(client => <SelectItem key={client.client_id} value={client.client_id}>{client.name}</SelectItem>)
                      ) : (
                        leads.map(lead => <SelectItem key={lead.lead_id} value={lead.lead_id}>{lead.name}</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input id="due_date" type="datetime-local" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} required data-testid="task-due-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="task-notes-input" rows={3} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="task-submit-button">{editingTask ? 'Update' : 'Create'} Task</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>All Tasks ({tasks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No tasks yet. Add your first task to get started!</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Related To</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.task_id} data-testid={`task-row-${task.task_id}`}>
                    <TableCell className="font-medium">{task.title}</TableCell>
                    <TableCell><Badge variant="outline">{task.task_type}</Badge></TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{getRelatedName(task.related_to, task.related_id)}</p>
                        <p className="text-xs text-slate-500 capitalize">{task.related_to}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(task.due_date)}</TableCell>
                    <TableCell><Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge></TableCell>
                    <TableCell><Badge className={getStatusColor(task.status)}>{task.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {task.status !== 'Completed' && (
                        <Button variant="ghost" size="icon" onClick={() => markComplete(task)} data-testid={`complete-task-${task.task_id}`}>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(task)} data-testid={`edit-task-${task.task_id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(task.task_id)} data-testid={`delete-task-${task.task_id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Tasks;